import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { CHECKOUT_API_HOSTNAME } from "../../../../env";
import fulfill from "../../../../fulfill";
import type { ProfileConfiguration } from "../../../../types/checkout";

const fetchPaymentProfile = async (
    key: string,
): Promise<ProfileConfiguration> => {
    const [accountId, , profileId] = key.split("/");
    return await fulfill.get({
        accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/admin/session/profiles/${profileId}`,
        handlers: {
            200: (profile) => profile,
        },
    });
};

const fetchDeletePaymentProfile = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            profileId: string;
        };
    },
): Promise<ProfileConfiguration> => {
    return await fulfill.delete({
        accountId: options.arg.accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/admin/session/profiles/${options.arg.profileId}`,
        handlers: {
            200: (profile) => profile,
        },
    });
};

const fetchPutPaymentProfile = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            profileId: string;
            profile: Partial<ProfileConfiguration>;
        };
    },
): Promise<{
    accountId: string;
    paymentProfile: ProfileConfiguration;
}> => {
    return await fulfill.put({
        accountId: options.arg.accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/admin/session/profiles/${options.arg.profileId}`,
        json: options.arg.profile,
        handlers: {
            200: (profile) => ({
                accountId: options.arg.accountId,
                paymentProfile: profile,
            }),
        },
    });
};

export const usePaymentProfile = (
    profileAccountId: string,
    profileId: string,
) => {
    const key = `${profileAccountId}/payment-profiles/${profileId}`;
    const {
        data: profile,
        error,
        isLoading,
    } = useSWR(key, () => fetchPaymentProfile(key));
    const {
        data: deletedProfile,
        error: deleteError,
        isMutating: isDeleting,
        trigger: deleteProfile,
    } = useSWRMutation(key, fetchDeletePaymentProfile);
    const {
        data: updatedProfile,
        error: updateError,
        isMutating: isUpdating,
        trigger: updateProfile,
    } = useSWRMutation(key, fetchPutPaymentProfile);

    return {
        profile,
        error,
        isLoading,
        deleteProfile,
        isDeleted: !!deletedProfile,
        deleteError,
        isDeleting,
        updateProfile,
        isUpdating,
        updateError,
        updatedProfile,
        isUpdated: !!updatedProfile,
    };
};
